import PropTypes from 'prop-types';
import withRecurlyFieldValidation from 'src/common/decorators/subscription/withRecurlyFieldValidation';
import styleGuide from 'src/common/styles/styleGuide';
import cssVariables from 'src/common/styles/variables';
import css from './recurlyField.module.scss';

/**
 * A custom form field component that is specific to the Recurly library. The field is styled
 * to behave like our our other payment form inputs.
 * @param recurlyFieldName {string} Name of recurly field. Has to be the one of the designated
 * data-recurly value attributes (number, cvv, month, year) so that the library can find the field
 * and inject an iFrame into it.
 * @param hasBeenFocused {boolean} Whether the field has been focused on at any time in the
 * form's lifecycle.
 * @param isValid {boolean} Whether the form field value is valid or not
 * @param errorMessage {string} Error message to show beneath the form field
 * @param disableField {boolean} Whether to disable the form field or not
 * @returns {ReactComponent}
 */
function RecurlyField({
  fieldName,
  hasBeenFocused,
  isValid,
  errorMessage,
  disableField,
  children,
}) {
  let fieldStyle = {
    ...styleGuide.textField,
    height: '48px',
  };
  if (hasBeenFocused) {
    fieldStyle = {
      ...fieldStyle,
      ...styleGuide.textFieldFocused,
    };
  }
  if (!isValid) {
    fieldStyle = {
      ...fieldStyle,
      ...styleGuide.errorState,
    };
  }

  let disableContainerStyles = {};
  if (disableField) {
    fieldStyle = {
      ...fieldStyle,
      border: 'none',
      outline: 'none',
      backgroundColor: cssVariables['--tunein-gray'],
      pointerEvents: 'none',
    };
    disableContainerStyles = {
      cursor: 'not-allowed',
    };
  }

  let errorMessageStyles = {};
  if (!isValid) {
    errorMessageStyles = {
      ...styleGuide.inlineError,
      fontFamily: cssVariables['--primary-font'],
    };
  }
  return (
    <div
      style={disableContainerStyles}
      className={css.recurlyField}
      data-testid={`recurly-${fieldName}-field-container`}
    >
      <div
        style={fieldStyle}
        data-testid={`recurly-${fieldName}-field-inner-container`}
      >
        {children}
      </div>
      {!isValid && errorMessage && (
        <p
          style={errorMessageStyles}
          data-testid={`recurly-${fieldName}-field-error`}
        >
          {errorMessage}
        </p>
      )}
    </div>
  );
}

RecurlyField.propTypes = {
  fieldName: PropTypes.string.isRequired,
  isValid: PropTypes.bool.isRequired,
  hasBeenFocused: PropTypes.bool.isRequired,
  children: PropTypes.object.isRequired,
  disableField: PropTypes.bool,
  errorMessage: PropTypes.string,
};

export default withRecurlyFieldValidation(RecurlyField);
